import { useEffect, useMemo, useState } from 'react';

import { getPagesContent, useAppSelector, useAuthFlow, useGetUserTeams, useLeaveTeam } from '@pkg/hooks';
import { useSearchParamsState } from '@pkg/hooks/useSearchParams';

import { useJoinTeam, useQueryTeamByInviteCode } from '@app/pages/FantasyWishlist/hooks';
import { useAuthSidebar, useFantasyMetadataStore, useFantasySubmissionStore } from '@app/store/zustand';
import { useFantasyReferralStore } from '@app/store/zustand/FantasyGame/Referrals';

export const useHandleAcceptInviteTeam = () => {
    const [panelOpened, setPanelOpened] = useState(false);
    const { mutate: leaveTeam } = useLeaveTeam();
    const { mutateAsync: joinTeam, isLoading: isLoadingJoin } = useJoinTeam();

    const { setParams } = useSearchParamsState<{ welcome?: string }>();

    const clearCode = useFantasyReferralStore((state) => state.clearCode);
    const inviteCode = useFantasyReferralStore((state) => state.inviteCode);

    const { data: teamData, isLoading: isLoadingTeamData } = useQueryTeamByInviteCode({
        inviteCode: inviteCode ?? '',
        disabled: !inviteCode,
    });

    const currentGame = useFantasyMetadataStore((state) => state.currentGame);
    const currentGameWishlistId = useFantasyMetadataStore((state) => state.currentGameWishlistId);

    const userId = useAppSelector((s) => s.user.userAccount?.user.id);

    const { data: userTeamsData, isLoading: isLoadingUserTeams } = useGetUserTeams(!inviteCode);

    const alreadyJoined = useMemo(() => {
        const userTeams = getPagesContent(userTeamsData?.pages);
        return userTeams.some((team) => {
            const isMember = team.membership.some((user) => user?.user_id === userId);
            if (!isMember) leaveTeam({ teamId: team.id });
            return isMember;
        });
    }, [leaveTeam, userId, userTeamsData?.pages]);

    useEffect(() => {
        if (!teamData && !isLoadingTeamData && inviteCode) {
            clearCode();
        }
    }, [clearCode, inviteCode, isLoadingTeamData, teamData]);

    useEffect(() => {
        if (inviteCode && teamData && !isLoadingUserTeams && !alreadyJoined && !isLoadingJoin) {
            joinTeam({
                value: {
                    team_id: teamData.id,
                    inviteCode: inviteCode,
                    competitionId: currentGame?.id || '',
                    wishlistId: currentGameWishlistId,
                },
            })
                .then((teamResponse) => {
                    if (teamResponse.ok) {
                        setParams('welcome', '1');
                    }
                })
                .finally(clearCode);
        }
    }, [
        alreadyJoined,
        clearCode,
        currentGame?.id,
        currentGameWishlistId,
        inviteCode,
        isLoadingJoin,
        isLoadingUserTeams,
        joinTeam,
        setParams,
        teamData,
    ]);

    const setShowSubmitModal = useFantasySubmissionStore((state) => state.setShowSubmitModal);
    const setPreventClose = useAuthSidebar((state) => state.setPreventClose);
    const preventClose = useAuthSidebar((state) => state.preventClose);

    const { protectedAction } = useAuthFlow();

    useEffect(() => {
        if (inviteCode && !panelOpened) {
            setPanelOpened(true);
            setPreventClose(true);
            protectedAction(() => {
                setShowSubmitModal(true);
                setPreventClose(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inviteCode, preventClose]);
};
